/**
 * Detects user authentication state from cookies, DOM attributes, and browser storage
 * Handles special cases like checkout success page authentication detection
 */

import { AuthenticationMarkers } from './interfaces';

export class AuthenticationDetector {
  
  /**
   * Determines if user is authenticated by checking cookie tokens and special checkout context
   * @returns True if user is authenticated, false otherwise
   */
  public isAuthenticated(): boolean {
    const cookieValue = this.getCookieValue('algolia_user_token');
    
    if (window.location.pathname.includes('checkout_success.php') && cookieValue) {
      return this.wasUserAuthenticatedDuringJourney();
    }
    
    return cookieValue !== null && cookieValue.startsWith('user_');
  }

  /**
   * Extracts authenticated user token by validating DOM attributes and cookie values
   * @returns The authenticated user token or null if not authenticated
   */
  public getAuthenticatedUserToken(): string | null {
    const bodyElement = document.body;
    const isLoggedInViaDOM = bodyElement?.hasAttribute('data-customer-logged-in') && 
                            bodyElement.getAttribute('data-customer-logged-in') === 'true';
    
    if (isLoggedInViaDOM) {
      const token = this.getCookieValue('algolia_user_token');
      return token && token.startsWith('user_') ? token : null;
    }
    
    return null;
  }

  /**
   * Analyzes multiple authentication indicators to determine checkout authentication status
   * @returns True if user was authenticated during journey, false otherwise
   */
  public wasUserAuthenticatedDuringJourney(): boolean {
    const authMarkers = [
      localStorage.getItem('user_authenticated'),
      localStorage.getItem('customer_id'),
      localStorage.getItem('user_id'),
      this.getCookieValue('customer_id'),
      this.getCookieValue('_autuserid2'),
      this.getCookieValue('user_id'),
      (window as Window & { CUSTOMER_ID?: string }).CUSTOMER_ID,
      (window as Window & { CURRENT_USER_ID?: string }).CURRENT_USER_ID,
      this.getCookieValue('PHPSESSID') && this.getCookieValue('customer_id'),
    ];
    
    return authMarkers.some(marker => marker !== null && marker !== undefined);
  }

  /**
   * Collects authentication evidence from localStorage, cookies, and window globals
   * @returns Object containing all authentication markers
   */
  public gatherAuthenticationMarkers(): AuthenticationMarkers {
    return {
      userAuthenticated: localStorage.getItem('user_authenticated'),
      customerId: localStorage.getItem('customer_id'),
      userId: localStorage.getItem('user_id'),
      cookieCustomerId: this.getCookieValue('customer_id'),
      autoUserId: this.getCookieValue('_autuserid2'),
      cookieUserId: this.getCookieValue('user_id'),
      windowCustomerId: (window as Window & { CUSTOMER_ID?: string }).CUSTOMER_ID,
      windowCurrentUserId: (window as Window & { CURRENT_USER_ID?: string }).CURRENT_USER_ID,
      hasSession: !!(this.getCookieValue('PHPSESSID') && this.getCookieValue('customer_id')),
    };
  }


  /**
   * Gets a cookie value by name
   * @param name - The name of the cookie to retrieve
   * @returns The cookie value or null if not found
   */
  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }
}